import { ActivityType } from '../activities/activity-type.model';
import { DayStatusCycleKind, ScheduleGroup } from '../settings/me.model';
import { getEnabledScheduleGroups, resolveScheduleGroupLabel } from './schedule-group.util';

export interface DayStatusCycleActivity {
  activityTypeId: string;
  name: string;
  daysSince: number | null;
  daysSinceLabel: string;
  doneThisCycle: boolean;
  notThisCycle: boolean;
}

export interface DayStatusCycleGroup {
  ordinal: ScheduleGroup['ordinal'];
  label: string;
  cycle: DayStatusCycleKind;
  daysSince: number | null;
  daysSinceLabel: string;
  doneCount: number;
  totalCount: number;
  doneThisCycle: boolean;
  activities: DayStatusCycleActivity[];
}

export interface DayStatusCycleSection {
  cycle: DayStatusCycleKind;
  label: string;
  doneCount: number;
  totalCount: number;
  hasActivities: boolean;
  groups: DayStatusCycleGroup[];
}

export interface DayStatusCycles {
  work: DayStatusCycleSection;
  life: DayStatusCycleSection;
}

export interface UpNextCycleGroup extends DayStatusCycleGroup {
  cycleLabel: string;
  leftCount: number;
}

const NEVER_SORT_KEY = Number.POSITIVE_INFINITY;
const DEFAULT_WORK_CYCLE_LABEL = 'Work';
const DEFAULT_LIFE_CYCLE_LABEL = 'Life';

export function resolveScheduleGroupCycle(group: Pick<ScheduleGroup, 'cycle'> | null | undefined): DayStatusCycleKind {
  return group?.cycle === 'LIFE' ? 'LIFE' : 'WORK';
}

export function formatDaysSince(daysSince: number | null): string {
  if (daysSince == null) {
    return 'Never';
  }
  if (daysSince <= 0) {
    return 'Today';
  }
  return `${daysSince}d`;
}

export function computeDaysSince(
  lastDoneAt: string | null | undefined,
  nowMs: number,
  timeZone: string
): number | null {
  if (!lastDoneAt) {
    return null;
  }
  const lastDoneMs = Date.parse(lastDoneAt);
  if (!Number.isFinite(lastDoneMs)) {
    return null;
  }
  const today = calendarDateKey(nowMs, timeZone);
  const lastDoneDate = calendarDateKey(lastDoneMs, timeZone);
  if (!today || !lastDoneDate) {
    return null;
  }
  const diffDays = Math.round((utcDateFromKey(today) - utcDateFromKey(lastDoneDate)) / 86_400_000);
  return Math.max(0, diffDays);
}

export function getDaysSinceAriaLabel(daysSince: number | null): string {
  if (daysSince == null) {
    return 'last done never';
  }
  if (daysSince <= 0) {
    return 'last done today';
  }
  return daysSince === 1 ? 'last done 1 day ago' : `last done ${daysSince} days ago`;
}

export function resolveCycleDisplayLabel(
  cycle: DayStatusCycleKind,
  workLabel?: string | null,
  lifeLabel?: string | null
): string {
  const customLabel = (cycle === 'LIFE' ? lifeLabel : workLabel)?.trim();
  if (customLabel) {
    return customLabel;
  }
  return cycle === 'LIFE' ? DEFAULT_LIFE_CYCLE_LABEL : DEFAULT_WORK_CYCLE_LABEL;
}

export function computeCycleDurationDays(
  startedAt: string | null | undefined,
  completedAt: string | null | undefined,
  timeZone: string
): number | null {
  const startedAtMs = parseInstant(startedAt);
  const completedAtMs = parseInstant(completedAt);
  if (startedAtMs == null || completedAtMs == null) {
    return null;
  }
  const startedDate = calendarDateKey(startedAtMs, timeZone);
  const completedDate = calendarDateKey(completedAtMs, timeZone);
  if (!startedDate || !completedDate) {
    return null;
  }
  const diffDays = Math.round((utcDateFromKey(completedDate) - utcDateFromKey(startedDate)) / 86_400_000);
  return Math.max(0, diffDays);
}

export function computeDayStatusCycles(
  scheduleGroups: ScheduleGroup[],
  activityTypes: ActivityType[],
  workCycleStartedAt: string | null | undefined,
  lifeCycleStartedAt: string | null | undefined,
  nowMs: number,
  timeZone: string,
  workCycleLabel?: string | null,
  lifeCycleLabel?: string | null
): DayStatusCycles {
  const enabledGroups = getEnabledScheduleGroups(scheduleGroups);
  const timeBlocks = activityTypes.filter((activity) => activity.kind === 'TIME_BLOCK');

  const groups = enabledGroups.flatMap((group) => {
    const cycle = resolveScheduleGroupCycle(group);
    const cycleStartedAt = cycle === 'LIFE' ? lifeCycleStartedAt : workCycleStartedAt;
    const activities = timeBlocks
      .filter((activity) => activity.scheduleType === group.ordinal)
      .map((activity) => toCycleActivity(activity, cycleStartedAt, nowMs, timeZone));
    if (activities.length === 0) {
      return [];
    }
    const daysSince = maxDaysSince(activities);
    const doneCount = activities.filter((activity) => activity.doneThisCycle || activity.notThisCycle).length;
    return [{
      ordinal: group.ordinal,
      label: resolveScheduleGroupLabel(group),
      cycle,
      daysSince,
      daysSinceLabel: formatDaysSince(daysSince),
      doneCount,
      totalCount: activities.length,
      doneThisCycle: doneCount === activities.length,
      activities
    } satisfies DayStatusCycleGroup];
  });

  return {
    work: buildSection(
      'WORK',
      resolveCycleDisplayLabel('WORK', workCycleLabel, lifeCycleLabel),
      groups.filter((group) => group.cycle === 'WORK')
    ),
    life: buildSection(
      'LIFE',
      resolveCycleDisplayLabel('LIFE', workCycleLabel, lifeCycleLabel),
      groups.filter((group) => group.cycle === 'LIFE')
    )
  };
}

export function isDoneThisCycle(
  lastDoneAt: string | null | undefined,
  cycleStartedAt: string | null | undefined
): boolean {
  const lastDoneMs = parseInstant(lastDoneAt);
  const cycleStartedMs = parseInstant(cycleStartedAt);
  return lastDoneMs != null && cycleStartedMs != null && lastDoneMs > cycleStartedMs;
}

export function sortByCycleTiers<T extends { doneThisCycle: boolean; daysSince: number | null }>(
  items: readonly T[],
  oldestFirst: boolean
): T[] {
  const undone = items.filter((item) => !item.doneThisCycle);
  const done = items.filter((item) => item.doneThisCycle);
  return [
    ...(oldestFirst ? sortByDaysSinceDesc(undone) : undone),
    ...sortByDaysSinceDesc(done)
  ];
}

export function resolveUpNextGroup(cycles: DayStatusCycles): UpNextCycleGroup | null {
  for (const section of [cycles.work, cycles.life]) {
    const group = section.groups.find((candidate) => !candidate.doneThisCycle);
    if (group) {
      return {
        ...group,
        cycleLabel: section.label,
        leftCount: Math.max(0, group.totalCount - group.doneCount)
      };
    }
  }
  return null;
}

function toCycleActivity(
  activity: ActivityType,
  cycleStartedAt: string | null | undefined,
  nowMs: number,
  timeZone: string
): DayStatusCycleActivity {
  const daysSince = computeDaysSince(activity.lastDoneAt, nowMs, timeZone);
  return {
    activityTypeId: activity.id,
    name: activity.name,
    daysSince,
    daysSinceLabel: formatDaysSince(daysSince),
    doneThisCycle: isDoneThisCycle(activity.lastDoneAt, cycleStartedAt),
    notThisCycle: isDoneThisCycle(activity.notThisCycleAt, cycleStartedAt)
  };
}

/**
 * The cycle section that setting this activity aside would complete (and so reset), or null.
 * Mirrors the backend: a cycle completes when every activity in its enabled groups is done or set aside.
 */
export function findCycleCompletedBySettingAside(
  cycles: DayStatusCycles,
  activityTypeId: string
): DayStatusCycleSection | null {
  for (const section of [cycles.work, cycles.life]) {
    const activities = section.groups.flatMap((group) => group.activities);
    const target = activities.find((activity) => activity.activityTypeId === activityTypeId);
    if (!target) {
      continue;
    }
    if (target.doneThisCycle || target.notThisCycle) {
      return null;
    }
    const othersResolved = activities
      .filter((activity) => activity.activityTypeId !== activityTypeId)
      .every((activity) => activity.doneThisCycle || activity.notThisCycle);
    return othersResolved ? section : null;
  }
  return null;
}

function sortByDaysSinceDesc<T extends { daysSince: number | null }>(items: readonly T[]): T[] {
  return items.slice().sort((left, right) => {
    const leftKey = left.daysSince == null ? NEVER_SORT_KEY : left.daysSince;
    const rightKey = right.daysSince == null ? NEVER_SORT_KEY : right.daysSince;
    return rightKey - leftKey;
  });
}

function sortGroups(groups: DayStatusCycleGroup[]): DayStatusCycleGroup[] {
  return [
    ...groups.filter((group) => !group.doneThisCycle),
    ...groups.filter((group) => group.doneThisCycle)
  ];
}

function maxDaysSince(activities: DayStatusCycleActivity[]): number | null {
  if (activities.some((activity) => activity.daysSince == null)) {
    return null;
  }
  return activities.reduce((max, activity) => Math.max(max, activity.daysSince ?? 0), 0);
}

function buildSection(
  cycle: DayStatusCycleKind,
  label: string,
  groups: DayStatusCycleGroup[]
): DayStatusCycleSection {
  const sorted = sortGroups(groups);
  const doneCount = sorted.reduce((sum, group) => sum + group.doneCount, 0);
  const totalCount = sorted.reduce((sum, group) => sum + group.totalCount, 0);
  return {
    cycle,
    label,
    doneCount,
    totalCount,
    hasActivities: totalCount > 0,
    groups: sorted
  };
}

function parseInstant(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function calendarDateKey(epochMs: number, timeZone: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: resolveTimeZone(timeZone),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date(epochMs));
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;
    if (!year || !month || !day) {
      return null;
    }
    return `${year}-${month}-${day}`;
  } catch {
    return calendarDateKey(epochMs, 'UTC');
  }
}

function utcDateFromKey(key: string): number {
  return Date.parse(`${key}T00:00:00.000Z`);
}

function resolveTimeZone(timeZone: string): string {
  const trimmed = timeZone?.trim();
  if (!trimmed) {
    return 'UTC';
  }
  try {
    Intl.DateTimeFormat('en-US', { timeZone: trimmed }).format(new Date());
    return trimmed;
  } catch {
    return 'UTC';
  }
}
